import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { ApiService } from '../../../../services/api.service';
import { AuthService } from '../../../../services/auth.service';
import { ToasterService } from '../../../../services/toaster.service';
import { UtilityService } from '../../../../services/utility.service';
import { CheckboxInputComponent } from '../../form/checkbox-input/checkbox-input.component';
import { DropdownInputComponent } from '../../form/dropdown-input/dropdown-input.component';
import { TextInputComponent } from '../../form/text-input/text-input.component';
import { routes } from '../../../../constants/routes';
import { UploadInputComponent } from '../../form/upload-input/upload-input.component';
import { InputLabelComponent } from '../../form/input-label/input-label.component';
import { ImageLazyLoadingDirective } from '../../../../directives/img-lazy-loading.directive';
import { FileObject } from '../../../../models/file-object.model';
import { unsubscribe } from '../../../../decorators/unsubscribe.decorator';
import { decryptData } from '../../../../constants/enc-dec.helper';
import { apiErrorHandler } from '../../../../constants/api-error-handler';
import { ApiErrorHandler } from '../../../../models/api-error-handler.model';
import { pageSeoInfo } from '../../../../constants/page-seo-info';
import { SeoService } from '../../../../services/seo.service';
import { OrgEntityType } from '../../../../constants/organization-enity-type';

@Component({
  selector: 'app-setup-organization',
  standalone: true,
  imports: [
    CheckboxInputComponent,
    TextInputComponent,
    DropdownInputComponent,
    ReactiveFormsModule,
    UploadInputComponent,
    InputLabelComponent,
    ImageLazyLoadingDirective,
  ],
  templateUrl: './setup-organization.component.html',
  styleUrl: './setup-organization.component.scss',
})
@unsubscribe()
export class SetupOrganizationComponent implements OnInit, ApiErrorHandler {
  public organizationForm!: FormGroup;
  private updateUserDataSub!: Subscription;

  private readonly routes = routes;
  public uploadFileData: { [key: string]: { url?: string } & FileObject } = {
    organizationLogo: undefined,
  };

  public readonly entityTypes = OrgEntityType;

  public readonly sectors = [
    {
      value: 'Agriculture, Forestry & Rural Development',
      key: 'Agriculture, Forestry & Rural Development',
    },
    {
      value: 'Aquaculture, Livestock & Fisheries',
      key: 'Aquaculture, Livestock & Fisheries',
    },
    { value: 'Arts, Culture & Tourism', key: 'Arts, Culture & Tourism' },
    {
      value: 'Chemical, Mining & Natural Resources',
      key: 'Chemical, Mining & Natural Resources',
    },
    { value: 'Commerce, Trade & Industry', key: 'Commerce, Trade & Industry' },
    {
      value: 'Communications, Media & Broadcasting',
      key: 'Communications, Media & Broadcasting',
    },
    {
      value: 'Defence & National Security',
      key: 'Defence & National Security',
    },
    {
      value: 'Education, Training & Skill Development',
      key: 'Education, Training & Skill Development',
    },
    {
      value: 'Energy, Power & Renewable Resources',
      key: 'Energy, Power & Renewable Resources',
    },
    {
      value: 'Environmental Conservation & Sustainability',
      key: 'Environmental Conservation & Sustainability',
    },
    {
      value: 'Finance, Banking & Insurance',
      key: 'Finance, Banking & Insurance',
    },
    {
      value: 'Food Production & Distribution',
      key: 'Food Production & Distribution',
    },
    {
      value: 'Governance, Administration & Law',
      key: 'Governance, Administration & Law',
    },
    {
      value: 'Healthcare, Wellness & Family Welfare',
      key: 'Healthcare, Wellness & Family Welfare',
    },
    {
      value: 'Housing, Urban Planning & Infrastructure',
      key: 'Housing, Urban Planning & Infrastructure',
    },
    {
      value: 'Science, Technology & Research Innovation',
      key: 'Science, Technology & Research Innovation',
    },
    {
      value: 'Social Justice & Community Empowerment',
      key: 'Social Justice & Community Empowerment',
    },
    {
      value: 'Transportation, Logistics & Mobility',
      key: 'Transportation, Logistics & Mobility',
    },
    {
      value: 'Youth Development, Sports & Empowerment',
      key: 'Youth Development, Sports & Empowerment',
    },
    {
      value: 'Global Relations & International Affairs',
      key: 'Global Relations & International Affairs',
    },
    { value: 'Other', key: 'Other' },
  ];

  private userDetails!: { userId: string };

  readonly organizationValidations = {
    mobileNo: {
      maxLength: 10,
    },
    message: {
      maxLength: 200,
    },
    address: {
      maxLength: 250,
    },
  };

  constructor(
    private router: Router,
    public toaster: ToasterService,
    private apiService: ApiService,
    private authService: AuthService,
    public utilityService: UtilityService,
    private activatedRoute: ActivatedRoute,
    private seoService: SeoService,
  ) {
    this.userDetails = <{ userId: string }>(
      this.activatedRoute.snapshot.queryParams
    );
  }

  ngOnInit() {
    this.initOrgnaizationForm();
    const { title, description, canonicalUrl } =
      pageSeoInfo['setupOrganisation'];
    this.seoService.setPageTitle(title);
    this.seoService.setPageDescription(description);
    this.seoService.setCanonicalUrl(canonicalUrl);
  }

  private initOrgnaizationForm() {
    this.organizationForm = new FormGroup({
      organizationName: new FormControl('', [Validators.required]),
      organizationSector: new FormControl('', [Validators.required]),
      entityType: new FormControl('', [Validators.required]),

      organizationAddress: new FormControl('', [
        Validators.required,
        Validators.maxLength(this.organizationValidations.address.maxLength),
      ]),
      organizationLogo: new FormControl(''),
      organizationWebsite: new FormControl('', [Validators.required]),
      isAgreeTnc: new FormControl('', [Validators.requiredTrue]),
    });
  }

  onFileUpload(imageProfileData: FileObject, fileType: string) {
    this.uploadFileData[fileType] = imageProfileData;
    if (imageProfileData) {
      this.uploadDocument(imageProfileData, fileType);
    }
  }

  onFileRemove(fileType: string) {
    this.uploadFileData[fileType] = undefined;
    this.organizationForm.get(fileType)?.reset();
  }

  uploadDocument(fileData: FileObject, fileType: string) {
    const uploadImageBody = {
      file_name: fileData?.fileName || '',
      image_url: fileData?.base64 || '',
      type: 'content',
    };

    this.apiService.uploadImage(uploadImageBody).subscribe({
      next: (res) => {
        this.organizationForm.get(fileType).setValue(encodeURI(res.url));
      },
      error: () => {
        this.toaster.error('Error while uploading file');
      },
    });
  }

  onPrivacyPolicyClick(event: any) {
    event.preventDefault();
    const url = this.router.serializeUrl(
      this.router.createUrlTree([
        `/${this.routes.home}/${this.routes.orgPrivacy}`,
      ]),
    );

    this.utilityService.openUrlInNewTab(url);
  }

  onUpdateProfile() {
    const isAgreeInvalid = this.organizationForm.get('isAgreeTnc')?.invalid;

    if (this.organizationForm.invalid) {
      this.organizationForm.markAllAsTouched();
      return;
    }

    if (isAgreeInvalid) {
      this.toaster.error(
        isAgreeInvalid
          ? 'You must agree to the Terms of Use and Privacy Policy before proceeding.'
          : 'Please upload all the required documents before submitting details.',
      );
    }

    const myProfileDetail = this.organizationForm.getRawValue();
    const bodyObj = {
      entityType: myProfileDetail.entityType || '',
      sector: myProfileDetail.organizationSector || '',
      name: myProfileDetail.organizationName || '',
      website: myProfileDetail.organizationWebsite || '',
      address: myProfileDetail.organizationAddress || '',
      logo: myProfileDetail.organizationLogo || '',
      userId: decryptData(this.userDetails.userId),
    };

    this.updateUserDataSub = this.apiService
      .submitOrganizationSteps(bodyObj, 'step2')
      .subscribe({
        next: (resp) => {
          this.toaster.success('Organization Details successfully updated.');

          this.authService.saveUserProfile(resp.data);
          this.authService.redirectToDashboard();
        },
        error: apiErrorHandler.bind(this),
      });
  }

  redirectToRegister() {
    this.router.navigate([routes.account, routes.login], { replaceUrl: true });
  }

  ngOnDestroy() {
    this.updateUserDataSub.unsubscribe();
  }
}
